// Drop this into App.js or src/components/ComparisonModule.js
import React, { useEffect, useMemo, useState } from "react";
import countryData from "../countryData.json";
import "./ComparisonTable.css";

export default function ComparisonTable({ initialCountry }) {
  const countriesByName = useMemo(() => {
    const m = new Map();
    countryData.forEach((c) => m.set(String(c.name).toLowerCase(), c));
    return m;
  }, []);

  const [selected, setSelected] = useState(() => {
    if (!initialCountry) return [];
    const c = countriesByName.get(String(initialCountry).toLowerCase());
    return c ? [c] : [];
  });

  const [pick, setPick] = useState("");
  const [visibleCategories, setVisibleCategories] = useState({
    basic: true,
    finance: true,
    banking: true,
    crypto: true,
    social: true,
  });

  useEffect(() => {
    if (!initialCountry) return;
    const c = countriesByName.get(String(initialCountry).toLowerCase());
    if (!c) return;
    setSelected((prev) => {
      const rest = prev.filter(
        (x) => x.name.toLowerCase() !== c.name.toLowerCase()
      );
      return [c, ...rest].slice(0, 5);
    });
  }, [initialCountry, countriesByName]);

  const remainingOptions = countryData.filter(
    (c) => !selected.find((s) => s.name.toLowerCase() === c.name.toLowerCase())
  );

  function addSelectedCountry(name) {
    if (!name) return;
    const c = countriesByName.get(String(name).toLowerCase());
    if (!c) return;
    setSelected((prev) => {
      if (prev.find((p) => p.name.toLowerCase() === c.name.toLowerCase()))
        return prev;
      return [...prev, c].slice(0, 5);
    });
    setPick("");
  }

  function removeCountry(name) {
    setSelected((prev) =>
      prev.filter((p) => p.name.toLowerCase() !== name.toLowerCase())
    );
  }

  function toggleCategory(cat) {
    setVisibleCategories((s) => ({ ...s, [cat]: !s[cat] }));
  }

  const FIELD_GROUPS = [
    {
      id: "basic",
      label: "Basic Info",
      fields: [
        { key: "name", label: "Country", render: (c) => c.name },
        { key: "population", label: "Population" },
        { key: "area", label: "Area" },
        {
          key: "currency",
          label: "Currency",
          render: (c) =>
            c.currency ? `${c.currency.name} (${c.currency.symbol})` : "-",
        },
        { key: "capital", label: "Capital" },
        { key: "language", label: "Language" },
      ],
    },
    {
      id: "finance",
      label: "Finance",
      fields: [
        { key: "gdp", label: "GDP" },
        { key: "medianIncome", label: "Median Income" },
        { key: "inflation", label: "Inflation Rate" },
        { key: "unemployment", label: "Unemployment Rate" },
        { key: "debtToGDP", label: "Debt-to-GDP" },
      ],
    },
    {
      id: "banking",
      label: "Banking",
      fields: [
        { key: "numberOfBanks", label: "Number of Banks" },
        { key: "centralBank", label: "Central Bank" },
        { key: "interestRate", label: "Interest Rate" },
        { key: "onlineBanking", label: "Online Banking Penetration" },
        { key: "cashDigitalRatio", label: "Cash vs Digital Ratio" },
      ],
    },
    {
      id: "crypto",
      label: "Cryptocurrency",
      fields: [
        { key: "cryptoAdoption", label: "Crypto Adoption" },
        { key: "cryptoExchanges", label: "Crypto Exchanges" },
        { key: "cryptoLegalStatus", label: "Legal Status" },
        { key: "stablecoinUsage", label: "Stablecoin Usage" },
        { key: "nftMarket", label: "NFT Market" },
      ],
    },
    {
      id: "social",
      label: "Social",
      fields: [
        { key: "costOfLiving", label: "Cost of Living Index" },
        { key: "averageRent", label: "Average Rent" },
        { key: "healthcareAccess", label: "Healthcare Access" },
        { key: "educationIndex", label: "Education Index" },
      ],
    },
  ];

  function getFieldValue(country, key) {
    if (!country) return "-";
    const val = country[key];
    if (val === undefined || val === null || val === "") return "-";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  }

  return (
    <div className="country-details">
      <section className="cc-note">
        <p>
          ⚠️ <strong>Note:</strong> The data shown is a{" "}
          <strong>snapshot from 2024–2025</strong>. Some numbers are estimated
          (+/-), but it provides a good baseline to understand the world.
        </p>
      </section>

      <h2>Comparison Dashboard</h2>

      {/* Controls */}
      <section className="cc-controls">
        <div className="cc-left">
          <p>
            <strong>Add Country:</strong>
          </p>

          <div className="cc-add-row">
            <select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">-- choose --</option>
              {remainingOptions.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => addSelectedCountry(pick)}
              disabled={!pick || selected.length >= 5}
            >
              Add
            </button>
          </div>

          <div className="cc-selected">
            {selected.map((c) => (
              <span key={c.name} className="cc-pill">
                {c.name}
                <button onClick={() => removeCountry(c.name)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="cc-right">
          <p>
            <strong>Toggle Sections:</strong>
          </p>
          <br></br>
          <div className="cc-filters">
            {FIELD_GROUPS.map((g) => (
              <label key={g.id}>
                <input
                  type="checkbox"
                  checked={!!visibleCategories[g.id]}
                  onChange={() => toggleCategory(g.id)}
                />
                {g.label}
              </label>
            ))}
          </div>
        </div>
      </section>

      {/* Table */}
      <section>
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              {selected.map((c) => (
                <th key={c.name}>{c.name}</th>
              ))}
              {selected.length === 0 && <th>–</th>}
            </tr>
          </thead>
          <tbody>
            {FIELD_GROUPS.filter((g) => visibleCategories[g.id]).map((g) => (
              <React.Fragment key={g.id}>
                <tr>
                  <td colSpan={1 + Math.max(1, selected.length)}>
                    <strong>{g.label}</strong>
                  </td>
                </tr>
                {g.fields.map((f) => (
                  <tr key={f.key}>
                    <td>{f.label}</td>
                    {selected.map((c) => (
                      <td key={c.name + f.key}>
                        {f.render ? f.render(c) : getFieldValue(c, f.key)}
                      </td>
                    ))}
                    {selected.length === 0 && <td>–</td>}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
